import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { KeyRound, CalendarCheck, Sparkles, Camera, LineChart, MessageCircle, Send } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { api } from "@/lib/api";
import { VACATION_WHATSAPP, waLinkTo } from "@/lib/brand";

const VANTAGGI = [
  { icon: Camera, title: "Annunci curati", text: "Foto e testi professionali, pubblicati sui principali portali di affitto breve." },
  { icon: CalendarCheck, title: "Gestione completa", text: "Prenotazioni, calendario, check-in e check-out: pensiamo a tutto noi." },
  { icon: Sparkles, title: "Pulizie e accoglienza", text: "Coordiniamo pulizie e accoglienza ospiti per un servizio impeccabile." },
  { icon: LineChart, title: "Prezzi ottimizzati", text: "Tariffe aggiornate in base alla stagione per valorizzare il tuo immobile." },
];

const TIPI = ["Appartamento", "Villa / Villetta", "Attico", "Trullo", "Casa indipendente", "Altro"];

export default function AffittoBreveOwner() {
  const [form, setForm] = useState({ nome: "", telefono: "", tipo: "Appartamento", zona: "", disponibilita: "" });
  const [consenso, setConsenso] = useState(false);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefono || !form.zona) { toast.error("Compila nome, telefono e paese/zona."); return; }
    if (!consenso) { toast.error("Accetta l'informativa privacy."); return; }
    setSending(true);
    try {
      await api.post("/public/leads", {
        tipo: "affitto_breve",
        contatto: { nome: form.nome, telefono: form.telefono, email: null },
        payload: { "Tipo immobile": form.tipo, "Paese / Zona": form.zona, Disponibilità: form.disponibilita || "—" },
        consensoPrivacy: true,
        provenienza: "Sezione affitto breve proprietari",
      });
      toast.success("Richiesta inviata! Ti ricontatteremo per la gestione.");
      setForm({ nome: "", telefono: "", tipo: "Appartamento", zona: "", disponibilita: "" });
      setConsenso(false);
    } catch { toast.error("Invio non riuscito, riprova."); }
    finally { setSending(false); }
  };

  const inp = "w-full h-12 rounded-xl border border-border-subtle px-3 outline-none focus:border-brand bg-white text-sm";

  return (
    <section id="affitto-breve" className="relative py-24 sm:py-28 bg-accent-subtle">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left */}
        <div className="lg:col-span-6">
          <Reveal>
            <Overline>Affida il tuo immobile · Affitto breve</Overline>
            <h2 className="font-display font-semibold text-anthracite text-5xl sm:text-6xl leading-[0.95] tracking-tight mt-5">
              Hai una casa? <span className="italic text-brand">Falla rendere.</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-anthracite/70 max-w-md">
              Affida a noi la gestione del tuo affitto breve: ci occupiamo di annunci, prenotazioni,
              ospiti e pulizie. Tu ricevi i vantaggi, senza pensieri.
            </p>
          </Reveal>

          <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {VANTAGGI.map((v, i) => (
              <Reveal key={v.title} delay={0.1 + i * 0.08}>
                <div className="flex gap-3 rounded-2xl bg-white border border-border-subtle p-4 h-full">
                  <span className="grid place-items-center h-10 w-10 shrink-0 rounded-xl bg-brand/10 text-brand"><v.icon className="h-5 w-5" /></span>
                  <span>
                    <span className="block font-semibold text-anthracite text-sm">{v.title}</span>
                    <span className="block text-sm text-anthracite/60 mt-0.5">{v.text}</span>
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-6">
          <Reveal delay={0.15}>
            <div className="rounded-3xl bg-white border border-border-subtle p-6 sm:p-8 shadow-[0_25px_60px_-30px_rgba(15,92,74,0.3)]">
              <div className="flex items-center gap-2 text-brand mb-1"><KeyRound className="h-5 w-5" /><span className="text-xs uppercase tracking-[0.2em] font-semibold">Richiedi la gestione</span></div>
              <h3 className="font-display text-3xl font-semibold text-anthracite">Affittalo con noi</h3>
              <form onSubmit={submit} className="mt-5 space-y-3" data-testid="affitto-breve-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input className={inp} placeholder="Nome e cognome" value={form.nome} onChange={set("nome")} data-testid="ab-nome" />
                  <input className={inp} placeholder="Numero di telefono" value={form.telefono} onChange={set("telefono")} data-testid="ab-telefono" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <select className={inp} value={form.tipo} onChange={set("tipo")} data-testid="ab-tipo">
                    {TIPI.map((t) => <option key={t}>{t}</option>)}
                  </select>
                  <input className={inp} placeholder="Paese / Zona" value={form.zona} onChange={set("zona")} data-testid="ab-zona" />
                </div>
                <input className={inp} placeholder="Disponibilità (es. tutto l'anno, da giugno...)" value={form.disponibilita} onChange={set("disponibilita")} data-testid="ab-disponibilita" />
                <label className="flex items-start gap-2 text-xs text-anthracite/60">
                  <input type="checkbox" checked={consenso} onChange={(e) => setConsenso(e.target.checked)} className="mt-0.5 accent-[#0F5C4A]" data-testid="ab-privacy" />
                  Acconsento al trattamento dei dati secondo l'informativa privacy.
                </label>
                <button type="submit" disabled={sending} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold py-4 hover:bg-brand-hover transition-colors disabled:opacity-50" data-testid="ab-submit">
                  <Send className="h-4 w-4" /> {sending ? "Invio..." : "Invia la richiesta"}
                </button>
                <a href={waLinkTo(VACATION_WHATSAPP, "Salve, vorrei affidarvi la gestione del mio immobile per affitti brevi.")} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-brand text-brand font-semibold py-4 hover:bg-brand hover:text-white transition-colors" data-testid="ab-whatsapp">
                  <MessageCircle className="h-4 w-4" /> Scrivici su WhatsApp
                </a>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
